// src/services/competencyQuizApi.js
//
// Round Training-1 (2026-10-26) — Competency Quiz (Training) client.
// Mirrors CompetencyQuizController (api/competency-quiz). The server picks the
// questions, keeps the answer key, runs the timer and scores; this client never
// sees a correct answer until the attempt is submitted.
import api from "../api";

// ─── Take ──────────────────────────────────────────────────────────────────
export async function getOverview() {
    const res = await api.get("competency-quiz/overview");
    return res.data;
}

/** Start a new attempt, or resume the open one. */
export async function startAttempt() {
    const res = await api.post("competency-quiz/attempts");
    return res.data;
}

export async function getAttempt(attemptId) {
    const res = await api.get(`competency-quiz/attempts/${attemptId}`);
    return res.data;
}

/** Save one question's answer and/or flag. Pass selectedKeys = [] to clear it. */
export async function saveAnswer(attemptId, { questionId, selectedKeys, isFlagged }) {
    const res = await api.put(`competency-quiz/attempts/${attemptId}/answer`, {
        QuestionId:   questionId,
        SelectedKeys: selectedKeys ?? null,
        IsFlagged:    isFlagged ?? null,
    });
    return res.data;
}

export async function submitAttempt(attemptId) {
    const res = await api.post(`competency-quiz/attempts/${attemptId}/submit`);
    return res.data;
}

export async function getResult(attemptId) {
    const res = await api.get(`competency-quiz/attempts/${attemptId}/result`);
    return res.data;
}

// ─── Results (everyone's) ──────────────────────────────────────────────────
export async function listResults({ page = 1, pageSize = 25, search = "", from = "", to = "" } = {}) {
    const res = await api.get("competency-quiz/results", {
        params: {
            Page: page,
            PageSize: pageSize,
            Search: search || undefined,
            from: from || undefined,
            to: to || undefined,
        },
    });
    return res.data;
}

// ─── Manage ────────────────────────────────────────────────────────────────
export async function getSettings() {
    const res = await api.get("competency-quiz/manage/settings");
    return res.data;
}

export async function saveSettings(settings) {
    const res = await api.put("competency-quiz/manage/settings", {
        Title:               settings.title,
        Description:         settings.description,
        QuestionsPerAttempt: Number(settings.questionsPerAttempt),
        TimeLimitMinutes:    settings.timeLimitMinutes === "" || settings.timeLimitMinutes == null ? null : Number(settings.timeLimitMinutes),
        StrongPercent:       Number(settings.strongPercent),
        ReviewPercent:       Number(settings.reviewPercent),
        IsActive:            !!settings.isActive,
    });
    return res.data;
}

export async function listQuestions({ search = "", includeRetired = true } = {}) {
    const res = await api.get("competency-quiz/manage/questions", {
        params: { search: search || undefined, includeRetired },
    });
    return res.data;
}

const questionBody = (q) => ({
    QuestionCode:   q.questionCode,
    Domain:         q.domain,
    Roles:          q.roles,
    QuestionType:   q.questionType,
    QuestionText:   q.questionText,
    Rationale:      q.rationale,
    SourceDocument: q.sourceDocument,
    SourceSection:  q.sourceSection,
    Difficulty:     q.difficulty,
    IsCritical:     !!q.isCritical,
    ReferenceUrl:   q.referenceUrl,
    IsActive:       q.isActive !== false,
    Options:        (q.options || []).map((o) => ({ Key: o.key, Text: o.text })),
    CorrectKeys:    q.correctKeys || [],
});

export async function createQuestion(q) {
    const res = await api.post("competency-quiz/manage/questions", questionBody(q));
    return res.data;
}

export async function updateQuestion(questionId, q) {
    const res = await api.put(`competency-quiz/manage/questions/${questionId}`, questionBody(q));
    return res.data;
}

export async function listAssignments() {
    const res = await api.get("competency-quiz/manage/assignments");
    return res.data;
}

export async function assign(empId) {
    const res = await api.post(`competency-quiz/manage/assignments/${encodeURIComponent(empId)}`);
    return res.data;
}

export async function unassign(empId) {
    const res = await api.delete(`competency-quiz/manage/assignments/${encodeURIComponent(empId)}`);
    return res.data;
}

// ─── Helpers shared by the pages ───────────────────────────────────────────
/** The API answers PascalCase; read either casing. */
export const pick = (o, ...keys) => {
    for (const k of keys) {
        if (o && o[k] !== undefined && o[k] !== null) return o[k];
    }
    return undefined;
};

/** Message from an axios error ({ Message } / { message } / { error }). */
export const errorText = (err, fallback = "Something went wrong.") => {
    const d = err?.response?.data;
    if (typeof d === "string" && d.trim()) return d;
    return pick(d, "Message", "message", "error", "title") || err?.message || fallback;
};

export const BAND_TONE = {
    "Strong": "success",
    "Review recommended": "warning",
    "Review": "warning",
    "Training need": "danger",
};
